import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { VideoPlayer } from '@/components/video-player';
import { VideoFeatures } from '@/store/videoFeatures';
import { Monitor, Play } from 'lucide-react';
import { FinalCountdownOverlay } from '@/components/video-player/FinalCountdownOverlay';
import { HeadlineAbove } from '@/components/video-player/ui/HeadlineAbove';
import { VideoFeedbackPreview } from '@/components/feature-configs/video-feedback/VideoFeedbackPreview';
import { isValidRedirectUrl } from '@/lib/redirect-url';

interface LivePreviewPanelProps {
  features: VideoFeatures;
  // Feature sendo editada no momento. Usada para liberar o overlay de preview da
  // Jornada de Conversão apenas quando essa aba está ativa.
  activeFeature?: string;
}

export const LivePreviewPanel: React.FC<LivePreviewPanelProps> = ({
  features,
  activeFeature
}) => {
  useLanguage();
  // O card da Contagem Final cobre o player inteiro. Por isso só entra no
  // preview quando é ESSE o painel aberto — senão ele tapa a edição de
  // qualquer outro recurso enquanto estiver ligado.
  const showCountdownPreview =
    features.finalCountdown?.enabled && activeFeature === 'finalCountdown';

  // Na seção do Feedback do Vídeo o card cobre o player: o autoplay (overlay +
  // vídeo tocando mudo) atrás dele só atrapalha. Desliga os dois só aqui no
  // preview — sem o autoPlay=false o <video autoPlay> tocaria sozinho (e com
  // som, já que o usuário interagiu com a página). O painel é remontado por
  // seção (key no SideBySideEditor), então nas outras o autoplay volta normal.
  const hideAutoplay = activeFeature === 'videoFeedback';
  const previewFeatures = useMemo<VideoFeatures>(
    () =>
      hideAutoplay
        ? {
            ...features,
            autoPlay: false,
            autoplayOverlay: features.autoplayOverlay && { ...features.autoplayOverlay, enabled: false },
          }
        : features,
    [features, hideAutoplay]
  );

  // Simulação da contagem (chips "Simular 3s / 10s / Reset"). Sem isso o card
  // fica congelado no valor configurado e não dá para ver a contagem correr.
  const [previewTime, setPreviewTime] = useState<number | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);

  useEffect(() => {
    if (!isSimulating || previewTime === null || previewTime <= 0) return;
    const interval = setInterval(() => {
      setPreviewTime(prev => (prev === null || prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [isSimulating, previewTime]);

  // Auto-reset 2s depois de zerar
  useEffect(() => {
    if (previewTime !== 0) return;
    const timeout = setTimeout(() => {
      setPreviewTime(null);
      setIsSimulating(false);
    }, 2000);
    return () => clearTimeout(timeout);
  }, [previewTime]);

  const startSimulation = useCallback((seconds: number) => {
    setPreviewTime(seconds);
    setIsSimulating(true);
  }, []);

  const resetSimulation = useCallback(() => {
    setPreviewTime(null);
    setIsSimulating(false);
  }, []);

  const countdownDisplayTime =
    previewTime !== null ? previewTime : (features.finalCountdown?.countdownSeconds ?? 10);

  // Mesma regra do player: o botão "Ir Agora" só existe quando há para onde ir
  // (`showButton && redirectMode !== 'warning' && URL http/https válida`). Sem
  // isto o preview mostra um botão funcional onde o espectador real não teria
  // nenhum. `onSkip` fica undefined nos demais casos — e aí o overlay oferece
  // o botão de dispensar, que é a saída.
  const countdownCanGo =
    features.finalCountdown?.redirectMode !== 'warning' &&
    isValidRedirectUrl(features.finalCountdown?.redirectUrl);
  const countdownShowsButton = !!features.finalCountdown?.showButton && countdownCanGo;

  // Se não há vídeo E countdown está inativo → placeholder padrão
  if (!features.videoUrl && !showCountdownPreview) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="text-center">
          <Monitor className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {translateInterfaceText("Preview Dinâmico")}</h3>
          <p className="text-sm text-muted-foreground">
            {translateInterfaceText("As alterações aparecerão aqui em tempo real")}</p>
        </div>
      </div>
    );
  }

  // Se não há vídeo MAS countdown está ativo → mostrar mock com overlay
  if (!features.videoUrl && showCountdownPreview) {
    return (
      <div className="h-full p-4 sm:p-6 pt-16 sm:pt-[6rem] flex flex-col">
        <div className="mb-6 sm:mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Monitor className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-medium text-foreground">{translateInterfaceText("Preview em Tempo Real")}</h3>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => startSimulation(3)}
                className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                  isSimulating && previewTime !== null && previewTime <= 3 && previewTime > 0
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]'
                }`}
              >
                {translateInterfaceText("Simular 3s")}</button>
              <button
                type="button"
                onClick={() => startSimulation(10)}
                className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                  isSimulating && previewTime !== null && previewTime > 3
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]'
                }`}
              >
                {translateInterfaceText("Simular 10s")}</button>
              <button
                type="button"
                onClick={resetSimulation}
                className="rounded-full px-2.5 py-0.5 text-[11px] font-medium bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0] transition-colors"
              >
                {translateInterfaceText("Reset")}</button>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            {translateInterfaceText("Simulação do card no final do vídeo")}</p>
        </div>
        
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-2xl">
            <div className="aspect-video bg-[#111] rounded-lg shadow-lg overflow-hidden relative">
              {/* Player Mock (fundo escuro) */}
              <div className="absolute inset-0 flex items-center justify-center">
                <Play className="w-20 h-20 text-gray-700" />
              </div>

              {/* Overlay de Countdown (preview) */}
              {features.finalCountdown && (
                <FinalCountdownOverlay
                  config={features.finalCountdown}
                  timeRemaining={countdownDisplayTime}
                  onRedirect={() => {}}
                  onSkip={countdownShowsButton ? () => {} : undefined}
                  onDismiss={resetSimulation}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Se há vídeo → renderizar VideoPlayer normal
  return (
    <div className="h-full p-4 pt-6 flex flex-col">
      <div className="sm:mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Monitor className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-medium text-foreground">{translateInterfaceText("Preview em Tempo Real")}</h3>
          </div>
          {showCountdownPreview && (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => startSimulation(3)}
                className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                  isSimulating && previewTime !== null && previewTime <= 3 && previewTime > 0
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]'
                }`}
              >
                {translateInterfaceText("Simular 3s")}</button>
              <button
                type="button"
                onClick={() => startSimulation(10)}
                className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                  isSimulating && previewTime !== null && previewTime > 3
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]'
                }`}
              >
                {translateInterfaceText("Simular 10s")}</button>
              <button
                type="button"
                onClick={resetSimulation}
                className="rounded-full px-2.5 py-0.5 text-[11px] font-medium bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0] transition-colors"
              >
                {translateInterfaceText("Reset")}</button>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          {showCountdownPreview
            ? translateInterfaceText("Simulação do card no final do vídeo")
            : translateInterfaceText("Todas as alterações são aplicadas instantaneamente")}
        </p>
      </div>
      
      <div className="flex-1 flex flex-col items-center justify-start overflow-y-auto py-6">
        <div className="w-full max-w-2xl">
          {/* Headline Above Video */}
          {features.headline?.enabled && (
            <HeadlineAbove headline={features.headline} />
          )}

          <div className="relative">
            <VideoPlayer
              key={`preview-live-${features.videoUrl || 'no-video'}`}
              features={previewFeatures}
              isPreviewMode
              showJourneyPreview={activeFeature === 'conversionJourney'}
              showActionButtonPreview={activeFeature === 'actionButton'}
              editingPanel={activeFeature ?? null}
            />
            {/* Contagem Final sobre o player real (o hook só dispara no `ended`
                do vídeo; aqui é a simulação do editor). */}
            {showCountdownPreview && features.finalCountdown && (
              <FinalCountdownOverlay
                config={features.finalCountdown}
                timeRemaining={countdownDisplayTime}
                onRedirect={() => {}}
                onSkip={countdownShowsButton ? () => {} : undefined}
                onDismiss={resetSimulation}
              />
            )}
            {/* Feedback Preview Overlay — z-40 pra ficar ACIMA do autoplay
                (zIndex 20 no AutoplayResizeWrapper; 10/15/20 nos templates
                legados), do ícone de play/pause (z-30) e das thumbnails (z-20),
                que vivem dentro do container do VideoPlayer sem stacking
                context próprio. Com z-10 o autoplay tapava o card. Espelha o
                player real, onde o FeedbackOverlay fica acima de tudo. Fica
                abaixo de z-50 pra não passar por cima de dialogs/popovers do
                app. */}
            {activeFeature === 'videoFeedback' && features.videoFeedback?.enabled && (
              <div className="absolute inset-0 z-40 pointer-events-none">
                <VideoFeedbackPreview config={features.videoFeedback} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
